<template>
  <div class="chart-card">
    <div class="chart-card__header">
      <div class="chart-card__heading">
        <h5 class="chart-card__title">{{ chart.name }}</h5>
        <div class="chart-card__meta">{{ rangeText }}</div>
      </div>
      <div class="chart-card__actions">
        <button type="button" class="btn btn-sm btn-outline-secondary" @click="$emit('toggle-width', chart)">
          {{ chart.width === 'half' ? 'Full width' : 'Half width' }}
        </button>
        <button type="button" class="btn btn-sm btn-outline-info" :disabled="loading" @click="load">Refresh</button>
        <button type="button" class="btn btn-sm btn-outline-warning" @click="$emit('edit', chart)">Edit</button>
        <button type="button" class="btn btn-sm btn-outline-danger" @click="$emit('delete', chart)">Delete</button>
      </div>
    </div>
    <div class="chart-card__body">
      <div v-if="!chart.series.length" class="text-muted small text-center py-4">
        This chart has no series. Click Edit to add one.
      </div>
      <div v-else class="chart-card__chart">
        <!-- key = kunci state: ganti seri, kunci Y & seri tersembunyi lama gak kebawa. -->
        <!-- Menimpa chart, bukan membongkarnya: zoom bertahan saat Refresh. -->
        <compare-chart
          :key="stateKey"
          :timestamps="result.timestamps"
          :datasets="result.datasets"
          :x-extent="xExtent"
          :sync-group="`custom-${chart.id}`"
          :tk-key-extra="stateKey"
          :file-base="fileBase"
          :file-range="fileRange"
          :long-range="longRange"
        />
        <div v-if="loading" class="chart-card__loading"><wave-loader /></div>
      </div>
    </div>
  </div>
</template>

<script>
import { formatDateTime } from '@/util/date';
import { CHART_TOOLTIP } from '@/util/apiDatetime';
import {
  INTERVAL_TEXT, resolveMainRange, resolveInterval, seriesKey, escapeHtml, buildFetchPlan, buildDatasets,
} from '@/util/chart/customChartModel';
import { fetchChartRows } from '@/util/chart/customChartFetch';
import CompareChart from '@/components/customChart/CompareChart.vue';

const DAY_MS = 86400000;

export default {
  name: 'ChartCard',
  components: { CompareChart },
  props: {
    chart: { type: Object, required: true },
    assets: { type: Array, default: () => [] },
    deviceNameByUid: { type: Object, default: null },
  },
  data() {
    return {
      loading: true,
      result: { timestamps: [], datasets: [] },
      mainRange: null,
      interval: 'hour',
      loadSeq: 0,
    };
  },
  computed: {
    assetNameById() {
      const out = {};
      this.assets.forEach((a) => { out[a.massId] = a.massName; });
      return out;
    },
    // Lebar & urutan gak butuh narik ulang data.
    dataSignature() {
      const { range, interval, series } = this.chart;
      return JSON.stringify({ range, interval, series });
    },
    // Ganti seri = state kunci Y & sembunyi yang lama gak ikut kebawa.
    stateKey() {
      return `${this.chart.id}:${this.chart.series.map(seriesKey).join(',')}`;
    },
    rangeText() {
      if (!this.mainRange) return '';
      const from = formatDateTime(this.mainRange.startMs, CHART_TOOLTIP, null, false);
      const to = formatDateTime(this.mainRange.endMs, CHART_TOOLTIP, null, false);
      return `${from} – ${to} · ${INTERVAL_TEXT[this.interval] || this.interval}`;
    },
    xExtent() {
      return this.mainRange ? [this.mainRange.startMs, this.mainRange.endMs] : null;
    },
    longRange() {
      return !!this.mainRange && this.mainRange.endMs - this.mainRange.startMs > DAY_MS;
    },
    fileBase() {
      const slug = String(this.chart.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      return slug || 'custom-chart';
    },
    fileRange() {
      return this.mainRange ? [this.mainRange.startMs, this.mainRange.endMs, this.interval] : null;
    },
  },
  watch: {
    dataSignature: 'load',
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loadSeq += 1;
      const seq = this.loadSeq;
      const isStale = () => seq !== this.loadSeq || this._isDestroyed;
      const nowMs = Date.now();
      const mainRange = resolveMainRange(this.chart.range, nowMs);
      const interval = resolveInterval(this.chart.interval, mainRange);
      // Load pertama: isi header langsung biar gak kosong pas loading.
      if (this.mainRange === null) {
        this.mainRange = mainRange;
        this.interval = interval;
      }
      this.loading = true;
      try {
        const plan = buildFetchPlan(this.chart, mainRange, interval, { nowMs, deviceNameByUid: this.deviceNameByUid });
        const { rowsByKey, statusByKey } = await fetchChartRows(plan, interval, isStale);
        // Hasil lama gak boleh nimpa Refresh yang lebih baru.
        if (isStale() || Object.values(statusByKey).includes('cancelled')) return;
        // Header & chart ikut bareng biar gak nyampur data lama sama rentang baru.
        this.mainRange = mainRange;
        this.interval = interval;
        this.result = buildDatasets({
          chart: this.chart,
          rowsByKey,
          statusByKey,
          mainRange,
          interval,
          nowMs,
          assetNameById: this.assetNameById,
          deviceNameByUid: this.deviceNameByUid,
        });
        const broken = this.result.datasets.filter((d) => d.status === 'failed' || d.status === 'truncated');
        // Toast merender HTML, nama chart dari user wajib di-escape.
        if (broken.length) this.$toast.warning(`${escapeHtml(this.chart.name)}: ${broken.length} series could not be loaded.`);
      } catch (e) {
        console.warn('[custom chart] muat gagal', e && e.message);
        if (seq === this.loadSeq) this.$toast.error(`${escapeHtml(this.chart.name)}: failed to load data.`);
      } finally {
        if (seq === this.loadSeq) this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.chart-card { background: var(--mz-surface); border: 1px solid var(--mz-border); border-radius: var(--mz-radius-sm); overflow: hidden; height: 100%; }
.chart-card__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
  padding: 12px 16px;
  background: var(--mz-surface-2);
  border-bottom: 1px solid var(--mz-border);
  cursor: grab;
}
.chart-card__title { margin: 0; font-size: var(--mz-fs-md); font-weight: 700; }
.chart-card__meta { font-size: var(--mz-fs-sm); color: var(--mz-text-subtle); margin-top: 2px; }
.chart-card__actions { display: flex; gap: 6px; flex-wrap: wrap; cursor: default; }
.chart-card__body { padding: 8px 16px 12px; }
.chart-card__chart { position: relative; min-height: 200px; }
.chart-card__loading {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--mz-surface-glass);
  z-index: 20;
}
</style>
